import { getChecks, updateCheck } from 'admin/actions/checks';
import { Button } from 'admin/components/atoms/Buttons';
import CSVDownloadButton from 'admin/components/atoms/CSVDownloadButton';
import FA from 'admin/components/atoms/FontAwesome';
import Headings from 'admin/components/atoms/Headings';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import { formatJstFromUtc, toQueryParams } from 'admin/utils';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import { closeAlertModal, openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import Loader from '../../atoms/Loader';
import FilterForm from './FilterForm';
import ListTable from './ListTable';
import QrDownloadWorker from './qr-download.worker.js';

const ContentWrapper = styled.div``;
const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
`;

const EMPTY_CHECKS = { rows: [], pagination: {} };

const CHECKS_CSV_HEADERS = [
  {
    label: 'チャージQRコードID',
    key: 'id',
  },
  {
    label: 'マネーフルID',
    key: 'money_full_id',
  },
  {
    label: 'チャージ元店舗フルID',
    key: 'sender_shop_id',
  },
  {
    label: 'チャージ元店舗名',
    key: 'sender_shop_name',
  },
  {
    label: '用途',
    key: 'note',
  },
  {
    label: 'ポイント負担先店舗フルID',
    key: 'bear_point_shop_id',
  },
  {
    label: 'ポイント負担先店舗名',
    key: 'bear_point_shop_name',
  },
  {
    label: 'チャージマネー',
    key: 'money_amount',
  },
  {
    label: 'チャージポイント',
    key: 'point_amount',
  },
  {
    label: '受け取り可能人数',
    key: 'usage_count_and_limit',
  },
  {
    label: '説明文',
    key: 'description',
  },
  {
    label: '開始日時',
    key: 'starts_at',
  },
  {
    label: '終了日時',
    key: 'expires_at',
  },
  {
    label: 'ポイント有効期限（日付指定）',
    key: 'point_expires_at',
  },
  {
    label: 'ポイント有効期限（相対日数指定）',
    key: 'point_expires_in_days',
  },
  {
    label: 'コードプレフィックス',
    key: 'code_prefix',
  },
];

const SERIAL_CODE_CSV_HEADERS = [
  {
    label: 'シリアルコード',
    key: 'serial_code',
  },
  {
    label: 'マネー',
    key: 'money_amount',
  },
  {
    label: 'ポイント',
    key: 'point_amount',
  },
];

const SearchResultInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;
const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
  margin-left: 15px;
  padding: 16px 0;
`;
const SearchResultCount = styled.span`
  flex: 1;
`;

const StyledCSVDownloadButton = styled(CSVDownloadButton)`
  margin: 0;
  padding: 0;
`;

const DownloadLoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 24px;
  position: relative;
  min-width: 380px;
  min-height: 240px;
`;

const CancelDownloadButton = styled(FA)`
  position: absolute;
  top: -20px;
  right: -16px;
  padding: 8px;
  cursor: pointer;
`;

const Checks = ({ history, className, user }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [checks, setChecks] = useState(EMPTY_CHECKS);
  const [filters, setFilters] = useState({});
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '販促管理', href: '', isCurrentPage: true },
    { value: 'チャージQRコード管理', href: '', isCurrentPage: true },
  ];
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const dispatch = useDispatch();

  const [worker, setWorker] = useState(null);
  const [downloadError, setDownloadError] = useState('none');

  const workerOnMessage = (e) => {
    const a = document.createElement('a');
    const { content, timestamp } = e.data;
    a.href = content;
    a.download = `check_${userPrivateMoney.name}_${timestamp}.zip`;
    a.click();
    dispatch(
      closeAlertModal({
        shouldCloseOnOverlayClick: true,
        useCancel: false,
        useConfirm: false,
      })
    );
  };

  useEffect(() => {
    return () => {
      worker?.terminate();
    };
  }, [worker]);

  const handleChangePage = (page) => {
    setIsLoading(true);
    getChecks(toQueryParams({ page, ...filters }, true))
      .then((newChecks) => {
        setChecks(newChecks);
      })
      .finally(() => setIsLoading(false))
      .catch(() => {});
  };

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsLoading(true);
    getChecks(toQueryParams({ page: 1, ...newFilters }, true))
      .then(setChecks)
      .finally(() => setIsLoading(false))
      .catch(() => setChecks(EMPTY_CHECKS));
  };

  const goToCreateChecks = () => {
    history.push('/promotion/checks/create');
  };

  const goToBulkCreateChecks = () => {
    history.push('/promotion/checks/bulk-create');
  };

  const numberOfDataMax = 30000;

  const showDownloadError = (type) => {
    setDownloadError(type);
    setTimeout(() => {
      setDownloadError('none');
    }, [5000]);
  };

  // per_page の上限までしか取得できず、超過分は警告なしに CSV から落ちるため発行前に止める
  const guardCSVDownloadLimit = (downloadCsv) => {
    if (checks.count > numberOfDataMax) {
      showDownloadError('csv-over-limit');
      return true;
    }
    return downloadCsv();
  };

  const onDownloadQRCodes = async () => {
    if (checks.count > 1000) {
      showDownloadError('over-limit');
      return;
    }

    const downloadWorker = new QrDownloadWorker();
    downloadWorker.onmessage = workerOnMessage;

    worker?.terminate();
    setWorker(downloadWorker);

    dispatch(
      openAlertModal({
        body: (
          <DownloadLoaderWrapper>
            <CancelDownloadButton
              icon="times"
              onClick={() => {
                dispatch(
                  closeAlertModal({
                    shouldCloseOnOverlayClick: true,
                    useCancel: false,
                    useConfirm: false,
                  })
                );
                downloadWorker?.terminate();
              }}
            />
            <Loader type="Oval" width={50} height={50} />
            <p>処理中です。しばらくお待ちください</p>
          </DownloadLoaderWrapper>
        ),
        shouldCloseOnOverlayClick: false,
        useCancel: false,
        useConfirm: false,
      })
    );

    const res = await getChecks(toQueryParams({ per_page: numberOfDataMax, ...filters }, true));

    downloadWorker.postMessage(res.rows);
  };

  const renderSerialCodeCSVDownloadButton = () => {
    if (!checks.count) return null;
    return (
      <StyledCSVDownloadButton
        csvFilePrefix="check_code_"
        csvHeaders={SERIAL_CODE_CSV_HEADERS}
        text="シリアルコード"
        customDownloadBehaviour={guardCSVDownloadLimit}
        fetchData={() =>
          getChecks(toQueryParams({ per_page: numberOfDataMax, ...filters }, true)).then((res) =>
            res.rows.map((row) => ({
              serial_code: row.serial_code ?? '',
              money_amount: row.money_amount,
              point_amount: row.point_amount,
            }))
          )
        }
      />
    );
  };

  const renderCSVDownloadButton = () => {
    if (checks.count) {
      return (
        <StyledCSVDownloadButton
          csvFilePrefix="check_"
          csvHeaders={CHECKS_CSV_HEADERS}
          customDownloadBehaviour={guardCSVDownloadLimit}
          fetchData={() => {
            return getChecks(
              toQueryParams(
                {
                  per_page: numberOfDataMax,
                  ...filters,
                },
                true
              )
            ).then((checks) =>
              checks.rows.map((row) => {
                const usageLimit = row.is_onetime ? 1 : row.usage_limit;

                // For one-time checks: count is 1 if used (disabled), 0 if not
                // For regular checks: use the row's usage_count
                let usageCount = row.usage_count;
                if (row.is_onetime) {
                  usageCount = row.is_disabled ? 1 : 0;
                }

                const remainingCount = usageLimit === null ? null : usageLimit - usageCount;
                return {
                  id: row.id,
                  money_full_id: row.private_money.id,
                  sender_shop_id: row.user.id,
                  sender_shop_name: row.user.name,
                  note: row.note,
                  bear_point_shop_id: row.bear_point_shop?.id ?? '',
                  bear_point_shop_name: row.bear_point_shop?.name ?? '',
                  point_amount: row.point_amount,
                  money_amount: row.money_amount,
                  usage_count_and_limit: usageLimit ? `${usageCount}/${usageLimit}` : '',
                  remaining_count: remainingCount,
                  description: row.description,
                  starts_at: formatJstFromUtc(row.starts_at),
                  expires_at: formatJstFromUtc(row.expires_at),
                  point_expires_at: formatJstFromUtc(row.point_expires_at),
                  point_expires_in_days: row.point_expires_in_days,
                  code_prefix: row.serial_code?.slice(0, 4) ?? '',
                };
              })
            );
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className={className}>
      <BreadCrumb crumbs={crumbs} />
      <Alert color="danger" isOpen={downloadError === 'over-limit'}>
        ダウンロード対象は1,000件以下にしてください
      </Alert>
      <Alert color="danger" isOpen={downloadError === 'csv-over-limit'}>
        一度にダウンロード可能な最大数（{numberOfDataMax.toLocaleString()}
        件）を超えています。条件を見直してください。
      </Alert>
      {user.isAllowedTo('write', 'check') ? (
        <ButtonWrapper>
          <Button onClick={goToCreateChecks} type="submit">
            チャージQRコードを新規発行
          </Button>
          <Button onClick={goToBulkCreateChecks}>一括登録</Button>
        </ButtonWrapper>
      ) : null}
      <Headings.h6>検索条件</Headings.h6>
      <FilterForm applyFilters={applyFilters} />
      <SearchResultInfo>
        <SearchResultTitle>検索結果</SearchResultTitle>
        {checks.count && (
          <>
            <SearchResultCount>件数：{checks.count}</SearchResultCount>
            {renderCSVDownloadButton()}
            <Button variant="muted-outline" onClick={onDownloadQRCodes}>
              <FA style={{ marginRight: '0.5em' }} icon="download" />
              QRコード
            </Button>
            {renderSerialCodeCSVDownloadButton()}
          </>
        )}
      </SearchResultInfo>
      <ContentWrapper>
        {isLoading ? (
          <LoaderWrapper>
            <Loader type="Oval" width={50} height={50} />
          </LoaderWrapper>
        ) : (
          <ListTable
            checks={checks.rows}
            updateCheck={updateCheck}
            pagination={checks.pagination}
            onChangePage={handleChangePage}
          />
        )}
      </ContentWrapper>
      <p>QRコードは株式会社デンソーウェーブの登録商標です</p>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(styled(Checks)`
  ${ButtonWrapper} {
    margin: 10px;
  }
`);
